// src/pages/forge/ProblemDetail.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { squadAccess } from '@/lib/problemBankAccess';
import { ProblemBankEntry } from '@/types/problemBank';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  AlertTriangle,
  Clock,
  Building2,
  MapPin,
  User,
  Phone,
  Mail,
  FileText,
  Image,
  Video,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Award,
  BarChart3,
  Target,
  ExternalLink,
  Info,
  XCircle,
  Camera,
  FileSpreadsheet,
  Tag,
  CalendarDays,
  Hash,
  Gauge,
  ChevronRight,
  Code,
  Loader2,
  Rocket,
  Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_COLORS: Record<string, string> = {
  lead: 'bg-gray-100 text-gray-700 border-gray-200',
  verified: 'bg-green-100 text-green-700 border-green-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  accepted: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  solved: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200'
};

// Helper function to safely parse JSON fields
function safeParseJSON(data: any, fieldName: string): any {
  if (!data) return {};
  const field = data[fieldName];
  if (!field) return {};
  if (typeof field === 'string') {
    try {
      return JSON.parse(field);
    } catch (e) {
      console.error(`Error parsing ${fieldName}:`, e);
      return {};
    }
  }
  return field || {};
}

// Helper function to safely get nested property with fallback
function safeGet<T>(obj: any, path: string, fallback: T): T {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result === undefined || result === null) return fallback;
    result = result[key];
  }
  return (result === undefined || result === null) ? fallback : result;
}

function parseProblemData(problem: any): ProblemBankEntry {
  // Parse all JSON fields safely
  const anchor = safeParseJSON(problem, 'anchor');
  const quantification = safeParseJSON(problem, 'quantification');
  const rootCauseAnalysis = safeParseJSON(problem, 'root_cause_analysis');
  const solutionPotential = safeParseJSON(problem, 'solution_potential');
  const verification = safeParseJSON(problem, 'verification');
  const priorityScore = problem.priority_score;
  const assignment = problem.assignment;
  const outcome = problem.outcome;
  
  return {
    ...problem,
    id: problem.id,
    problemId: problem.problem_id,
    title: problem.title || 'Untitled',
    status: problem.status || 'lead',
    dateDiscovered: problem.date_discovered,
    source: problem.source,
    category: problem.category || [],
    industrySector: problem.industry_sector,
    anchor: anchor,
    quantification: quantification,
    rootCauseAnalysis: rootCauseAnalysis,
    solutionPotential: solutionPotential,
    verification: verification,
    priorityScore: priorityScore,
    assignment: assignment,
    outcome: outcome,
    createdAt: problem.created_at,
    updatedAt: problem.updated_at,
    assigned_to_squad_id: problem.assigned_to_squad_id,
    assigned_at: problem.assigned_at,
    selected_by_squad_id: problem.selected_by_squad_id,
    selected_at: problem.selected_at,
    selected_by_member_id: problem.selected_by_member_id
  };
}

// Helper function to format currency
function formatCurrency(value: number): string {
  if (!value || isNaN(value)) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
}

export default function ForgeProblemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { squadUser } = useSquadAuth();
  
  const [problem, setProblem] = useState<ProblemBankEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [isAlreadyAccepted, setIsAlreadyAccepted] = useState(false);
  const [isAssignedToThisSquad, setIsAssignedToThisSquad] = useState(false);

  useEffect(() => {
    if (id) {
      loadProblem();
    } else {
      setError('No problem ID provided');
      setIsLoading(false);
    }
  }, [id]);

  const loadProblem = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('🔍 Loading problem with ID:', id);
      
      const rawData = await squadAccess.getProblemDetails(id!);
      console.log('🔍 Raw problem data:', rawData);
      
      if (!rawData) {
        setError('Problem not found');
        setIsLoading(false);
        return;
      }
      
      const parsedProblem = parseProblemData(rawData);
      console.log('🔍 Parsed problem:', parsedProblem);
      
      setProblem(parsedProblem);
      
      // Check if this problem is already accepted by this squad
      if (parsedProblem.selected_by_squad_id === squadUser?.squadId) {
        setIsAlreadyAccepted(true);
      }
      
      // Check if this problem is assigned to this squad
      if (parsedProblem.assigned_to_squad_id === squadUser?.squadId) {
        setIsAssignedToThisSquad(true);
      }
      
    } catch (error) {
      console.error('Error loading problem:', error);
      setError(error instanceof Error ? error.message : 'Failed to load problem');
      toast({
        title: 'Error',
        description: 'Failed to load problem details',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptProblem = async () => {
    if (!problem || !squadUser) return;
    
    setIsAccepting(true);
    try {
      const { error: updateError } = await supabase
        .from('problem_bank')
        .update({
          selected_by_squad_id: squadUser.squadId,
          selected_at: new Date().toISOString(),
          selected_by_member_id: squadUser.memberId,
          status: 'accepted'
        })
        .eq('id', problem.id);

      if (updateError) throw updateError;

      toast({
        title: 'Problem Accepted!',
        description: 'Your 90-day journey has begun. The problem has been added to your squad\'s dashboard.',
      });
      
      setShowAcceptDialog(false);
      setIsAlreadyAccepted(true);
      
      // Refresh problem data
      await loadProblem();
      
      // Navigate to progress tracker to show the timeline
      navigate('/forge/progress');
      
    } catch (error) {
      console.error('Error accepting problem:', error);
      toast({
        title: 'Error',
        description: 'Failed to accept problem. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsAccepting(false);
    }
  };

  const getPriorityColor = (tier: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Determine button state
  const getButtonState = () => {
    if (!squadUser) return { show: false, text: '', disabled: true, onClick: undefined };
    if (isAlreadyAccepted) return { show: true, text: 'Problem Accepted', disabled: true, variant: 'outline' as const, icon: CheckCircle };
    if (!isAssignedToThisSquad) return { show: true, text: 'Not Assigned to Your Squad', disabled: true, variant: 'outline' as const, icon: AlertTriangle };
    return { show: true, text: 'Accept Problem', disabled: false, variant: 'default' as const, icon: Handshake, onClick: () => setShowAcceptDialog(true) };
  };

  const buttonState = getButtonState();

  // Safe access helpers for rendering
  const getAnnualLoss = () => {
    return safeGet(problem?.quantification, 'totalLossPerYear', 0);
  };

  const getMarketSize = () => {
    return safeGet(problem?.solutionPotential, 'marketSizeKenya', 0);
  };

  const getPaybackMonths = () => {
    return safeGet(problem?.solutionPotential, 'anchorPaybackMonths', 0);
  };

  const getDevCost = () => {
    return safeGet(problem?.solutionPotential, 'estimatedDevCost', 0);
  };

  const getAnchorName = () => {
    return safeGet(problem?.anchor, 'name', 'Unknown');
  };

  const getAnchorLocation = () => {
    return safeGet(problem?.anchor, 'location', 'Unknown');
  };

  const getAnchorContactName = () => {
    return safeGet(problem?.anchor, 'contactPerson.name', 'N/A');
  };

  const getAnchorContactTitle = () => {
    return safeGet(problem?.anchor, 'contactPerson.title', '');
  };

  const getAnchorContactEmail = () => {
    return safeGet(problem?.anchor, 'contactPerson.email', '');
  };

  const getAnchorContactPhone = () => {
    return safeGet(problem?.anchor, 'contactPerson.phone', '');
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-12 pb-12 text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <p className="text-xl font-semibold text-red-700 mb-2">Problem Not Found</p>
            <p className="text-sm text-red-600">{error || 'The problem you\'re looking for doesn\'t exist.'}</p>
            <Button variant="outline" className="mt-6" onClick={() => navigate('/forge/problem-bank')}>
              Back to Problem Bank
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button variant="ghost" onClick={() => navigate('/forge/dashboard')} className="h-auto p-0">
          Dashboard
        </Button>
        <ChevronRight className="h-3 w-3" />
        <Button variant="ghost" onClick={() => navigate('/forge/problem-bank')} className="h-auto p-0">
          Problem Bank
        </Button>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium">Problem Details</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{problem.title}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={STATUS_COLORS[problem.status] || 'bg-gray-100'}>
              {problem.status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
            {problem.priorityScore && (
              <Badge className={getPriorityColor(problem.priorityScore.priorityTier)}>
                {problem.priorityScore.priorityTier?.toUpperCase()} PRIORITY
              </Badge>
            )}
            <span className="text-muted-foreground flex items-center gap-1">
              <Hash className="h-4 w-4" />
              {problem.problemId}
            </span>
            <span className="text-muted-foreground flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              Discovered: {problem.dateDiscovered ? new Date(problem.dateDiscovered).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          {buttonState.show && (
            <Button 
              variant={buttonState.variant}
              onClick={buttonState.onClick}
              disabled={buttonState.disabled}
              className={!buttonState.disabled ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {buttonState.icon && <buttonState.icon className="h-4 w-4 mr-2" />}
              {buttonState.text}
            </Button>
          )}
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Priority Score Banner */}
      {problem.priorityScore && (
        <Card className={`border-l-4 ${
          problem.priorityScore.priorityTier === 'critical' 
            ? 'border-l-red-500 bg-red-50' 
            : problem.priorityScore.priorityTier === 'high'
            ? 'border-l-orange-500 bg-orange-50'
            : 'border-l-yellow-500 bg-yellow-50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <Gauge className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Priority Score: {problem.priorityScore.totalScore?.toFixed(1) || 'N/A'}/5.0</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-red-600 font-medium">{formatCurrency(getAnnualLoss())}</span>
              <span className="text-muted-foreground">loss</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-blue-600 font-medium">{getMarketSize()} factories</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-green-600 font-medium">{getPaybackMonths()}mo</span>
              <span className="text-muted-foreground">payback</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Annual Loss</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(getAnnualLoss())}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Market Size</p>
            <p className="text-2xl font-bold text-blue-600">{getMarketSize()} factories</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Payback Period</p>
            <p className="text-2xl font-bold text-green-600">{getPaybackMonths()} months</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Dev Cost</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(getDevCost())}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="quantification">Quantification</TabsTrigger>
          <TabsTrigger value="root-cause">Root Cause</TabsTrigger>
          <TabsTrigger value="solution">Solution</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Anchor Company Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Anchor Company
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg">{getAnchorName()}</h3>
                    <div className="space-y-1 mt-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{getAnchorLocation()}</span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div>
                      <p className="font-medium">{getAnchorContactName()}</p>
                      <p className="text-xs text-muted-foreground">{getAnchorContactTitle()}</p>
                    </div>
                    <div className="space-y-2">
                      {getAnchorContactEmail() && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${getAnchorContactEmail()}`} className="hover:underline text-blue-600">
                            {getAnchorContactEmail()}
                          </a>
                        </div>
                      )}
                      {getAnchorContactPhone() && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${getAnchorContactPhone()}`} className="hover:underline">
                            {getAnchorContactPhone()}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Priority Score Breakdown Card */}
            {problem.priorityScore && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Priority Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Loss Magnitude</span>
                        <span className="font-medium">{problem.priorityScore.lossMagnitude?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.lossMagnitude?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Anchor Urgency</span>
                        <span className="font-medium">{problem.priorityScore.anchorUrgency?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.anchorUrgency?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Solution Feasibility</span>
                        <span className="font-medium">{problem.priorityScore.solutionFeasibility?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.solutionFeasibility?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Market Size</span>
                        <span className="font-medium">{problem.priorityScore.marketSize?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.marketSize?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Relationship</span>
                        <span className="font-medium">{problem.priorityScore.relationshipStrength?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.relationshipStrength?.score || 0) * 20} className="h-2" />
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Score</span>
                      <Badge className={getPriorityColor(problem.priorityScore.priorityTier)}>
                        {problem.priorityScore.totalScore?.toFixed(1) || 0}/5.0
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Categories & Tags Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Categories & Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {problem.category?.map((cat: string, i: number) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {cat?.replace('_', ' ') || cat}
                      </Badge>
                    ))}
                    {(!problem.category || problem.category.length === 0) && (
                      <span className="text-sm text-muted-foreground">No categories</span>
                    )}
                  </div>
                </div>
                <Separator orientation="vertical" className="h-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Source</p>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    <Code className="h-3 w-3 mr-1" />
                    {problem.source?.replace('_', ' ') || 'Unknown'}
                  </Badge>
                </div>
                <Separator orientation="vertical" className="h-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Industry</p>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {problem.industrySector?.replace('_', ' ') || 'Other'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Full Problem Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Problem Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line text-sm leading-relaxed">
                {problem.quantification?.description || 'No description available'}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quantification Tab */}
        <TabsContent value="quantification">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Loss Quantification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/30 p-4 rounded-lg border">
                <p className="whitespace-pre-line text-sm">
                  {problem.quantification?.calculationMethodology || 'Not available'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3">Data Sources</h4>
                  <ul className="space-y-2">
                    {problem.quantification?.dataSources?.map((source: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span>{source}</span>
                      </li>
                    ))}
                    {(!problem.quantification?.dataSources || problem.quantification.dataSources.length === 0) && (
                      <li className="text-sm text-muted-foreground">No data sources listed</li>
                    )}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3">Confidence Level</h4>
                  <Badge className={
                    problem.quantification?.confidenceLevel === 'high' 
                      ? 'bg-green-100 text-green-700 border-green-200 px-3 py-1'
                      : problem.quantification?.confidenceLevel === 'medium'
                      ? 'bg-yellow-100 text-yellow-700 border-yellow-200 px-3 py-1'
                      : 'bg-red-100 text-red-700 border-red-200 px-3 py-1'
                  }>
                    {problem.quantification?.confidenceLevel?.toUpperCase() || 'LOW'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Root Cause Tab */}
        <TabsContent value="root-cause">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Root Cause Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 mb-3">
                  {problem.rootCauseAnalysis?.rootCauseCategory || 'Unknown'}
                </Badge>
                <p className="text-sm leading-relaxed">{problem.rootCauseAnalysis?.rootCauseDetails || 'Not available'}</p>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="text-sm font-medium mb-3">5 Whys Analysis</h4>
                <div className="space-y-3">
                  {problem.rootCauseAnalysis?.fiveWhys?.map((why: any, i: number) => (
                    <div key={i} className="pl-4 border-l-4 border-blue-200">
                      <p className="text-xs font-medium text-blue-600">Why {why.level}?</p>
                      <p className="text-sm mt-1">{why.answer}</p>
                    </div>
                  ))}
                  {(!problem.rootCauseAnalysis?.fiveWhys || problem.rootCauseAnalysis.fiveWhys.length === 0) && (
                    <p className="text-sm text-muted-foreground">No 5 Whys analysis available</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Solution Tab */}
        <TabsContent value="solution">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Solution Hypothesis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm leading-relaxed whitespace-pre-line">
                {problem.solutionPotential?.hypothesis || 'Not available'}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {problem.solutionPotential?.keyFeatures?.map((feature: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {(!problem.solutionPotential?.keyFeatures || problem.solutionPotential.keyFeatures.length === 0) && (
                      <li className="text-sm text-muted-foreground">No key features listed</li>
                    )}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3">MVP Features</h4>
                  <ul className="space-y-2">
                    {problem.solutionPotential?.mvpFeatures?.map((feature: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {(!problem.solutionPotential?.mvpFeatures || problem.solutionPotential.mvpFeatures.length === 0) && (
                      <li className="text-sm text-muted-foreground">No MVP features listed</li>
                    )}
                  </ul>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-medium mb-3">Pricing Model</h4>
                <p className="text-sm p-3 bg-muted/30 rounded-lg border">
                  {problem.solutionPotential?.estimatedPriceModel || 'Not specified'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evidence Tab */}
        <TabsContent value="evidence">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Evidence Files
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Photos & Videos */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photos & Videos ({problem.verification?.photoVideoEvidence?.length || 0})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {problem.verification?.photoVideoEvidence?.map((url: string, i: number) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
                    >
                      {url?.endsWith('.mp4') ? (
                        <Video className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Image className="h-4 w-4 text-green-600" />
                      )}
                      <span className="text-sm truncate flex-1">Evidence {i + 1}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </a>
                  ))}
                  {(!problem.verification?.photoVideoEvidence || problem.verification.photoVideoEvidence.length === 0) && (
                    <p className="text-sm text-muted-foreground col-span-full">No photo or video evidence</p>
                  )}
                </div>
              </div>

              {/* Data Files */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Data Files ({problem.verification?.dataFilesReceived?.length || 0})
                </h4>
                <div className="space-y-2">
                  {problem.verification?.dataFilesReceived?.map((file: any, i: number) => (
                    <a
                      key={i}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted transition-colors"
                    >
                      <FileText className="h-4 w-4 text-purple-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{file.type || 'Document'}</p>
                        {file.description && (
                          <p className="text-xs text-muted-foreground">{file.description}</p>
                        )}
                      </div>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </a>
                  ))}
                  {(!problem.verification?.dataFilesReceived || problem.verification.dataFilesReceived.length === 0) && (
                    <p className="text-sm text-muted-foreground">No data files attached</p>
                  )}
                </div>
              </div>

              {/* Anchor Acknowledgment */}
              <div className="bg-muted/30 p-4 rounded-lg border">
                <h4 className="text-sm font-medium mb-3">Anchor Acknowledgment</h4>
                {problem.verification?.anchorAcknowledgment?.obtained ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">
                      Confirmed via {problem.verification.anchorAcknowledgment.method || 'email'} on{' '}
                      {problem.verification.anchorAcknowledgment.date ? new Date(problem.verification.anchorAcknowledgment.date).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm">Pending anchor acknowledgment</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Accept Problem Confirmation Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-green-600" />
              Accept This Problem?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to accept this problem for your squad?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/30 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Problem: {problem.title}</p>
              <p className="text-xs text-muted-foreground">
                Accepting this problem will start your squad's 90-day countdown. 
                You will be able to track progress, milestones, and deliverables from your dashboard.
              </p>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-700">
                ⚠️ Once accepted, this problem will be locked to your squad. 
                Make sure your team is ready to commit to solving this problem.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAcceptProblem} 
              disabled={isAccepting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <Handshake className="mr-2 h-4 w-4" />
                  Accept Problem
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}